import {
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import "../../../assets/css/table/Table.css";
import TableHorizontalScrollTop from "./TableHorizontalScrollTop";

type TableHorizontalScrollContainerProps = {
  children: ReactNode;
  className?: string;
  /** Re-run overflow measurements when content changes. */
  measureKey?: string | number;
  /** Keep the top horizontal bar visible while the table body scrolls vertically. */
  maxBodyHeight?: string;
};

export default function TableHorizontalScrollContainer({
  children,
  className = "",
  measureKey,
  maxBodyHeight,
}: TableHorizontalScrollContainerProps): JSX.Element {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [hasHorizontalScroll, setHasHorizontalScroll] = useState(false);
  const [horizontalContentWidth, setHorizontalContentWidth] = useState(0);
  const [horizontalViewportWidth, setHorizontalViewportWidth] = useState(0);
  const [horizontalScrollLeft, setHorizontalScrollLeft] = useState(0);

  const horizontalMaxScrollLeft = Math.max(
    horizontalContentWidth - horizontalViewportWidth,
    0,
  );

  const horizontalScrollThumb = useMemo(() => {
    if (
      !hasHorizontalScroll ||
      horizontalContentWidth <= 0 ||
      horizontalViewportWidth <= 0
    ) {
      return { thumbWidth: 0, thumbLeft: 0 };
    }

    const thumbWidth = Math.max(
      32,
      (horizontalViewportWidth / horizontalContentWidth) *
        horizontalViewportWidth,
    );
    const maxThumbLeft = Math.max(horizontalViewportWidth - thumbWidth, 0);
    const thumbLeft =
      horizontalMaxScrollLeft > 0
        ? (horizontalScrollLeft / horizontalMaxScrollLeft) * maxThumbLeft
        : 0;

    return { thumbWidth, thumbLeft };
  }, [
    hasHorizontalScroll,
    horizontalContentWidth,
    horizontalViewportWidth,
    horizontalScrollLeft,
    horizontalMaxScrollLeft,
  ]);

  const handleHorizontalScrollLeftChange = useCallback((scrollLeft: number) => {
    const el = scrollRef.current;
    if (!el) {
      return;
    }
    el.scrollLeft = scrollLeft;
    setHorizontalScrollLeft(scrollLeft);
  }, []);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) {
      return;
    }
    setHorizontalScrollLeft(el.scrollLeft);
  }, []);

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) {
      return;
    }

    const measureHorizontalScroll = () => {
      const table = el.querySelector("table");
      const contentWidth = Math.max(table?.scrollWidth ?? 0, el.scrollWidth);
      const viewportWidth = el.clientWidth;
      const canScroll = contentWidth - viewportWidth > 1;

      setHasHorizontalScroll(canScroll);
      setHorizontalContentWidth(contentWidth);
      setHorizontalViewportWidth(viewportWidth);
      setHorizontalScrollLeft(el.scrollLeft);
    };

    const scheduleMeasure = () => {
      window.requestAnimationFrame(measureHorizontalScroll);
    };

    scheduleMeasure();
    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(scheduleMeasure)
        : null;
    resizeObserver?.observe(el);
    const table = el.querySelector("table");
    if (table) {
      resizeObserver?.observe(table);
    }
    window.addEventListener("resize", scheduleMeasure);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener("resize", scheduleMeasure);
    };
  }, [measureKey]);

  return (
    <div
      className={`table-responsive tabledata w-full min-w-0 ${
        maxBodyHeight ? "flex flex-col min-h-0" : ""
      } ${className}`.trim()}
    >
      <TableHorizontalScrollTop
        visible={hasHorizontalScroll}
        trackWidth={horizontalViewportWidth}
        thumbWidth={horizontalScrollThumb.thumbWidth}
        thumbLeft={horizontalScrollThumb.thumbLeft}
        maxScrollLeft={horizontalMaxScrollLeft}
        onScrollLeftChange={handleHorizontalScrollLeftChange}
      />
      <div
        className={maxBodyHeight ? "min-h-0 overflow-y-auto" : undefined}
        style={maxBodyHeight ? { maxHeight: maxBodyHeight } : undefined}
      >
        <div
          ref={scrollRef}
          className="w-full min-w-0 overflow-x-auto"
          onScroll={handleScroll}
        >
          {children}
        </div>
      </div>
    </div>
  );
}
